import { useState, useEffect, useCallback, useMemo } from "react";
import { Routes, Route, useNavigate } from "react-router-dom";
import "@/App.css";
import axios from "axios";
import { Toaster, toast } from "sonner";
import PaymentsReport from "./PaymentsReport";
import PharmacyReport from "./PharmacyReport";
import InventoryReport from "./InventoryReport";
import BillingReport from "./BillingReport";
import RackBinLocation from "./RackBinLocation";
import { 
  Users, 
  BedDouble, 
  IndianRupee, 
  FileText, 
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  Download,
  Search,
  Filter,
  ChevronDown,
  ChevronUp,
  Calendar,
  RefreshCw,
  X,
  Eye,
  Activity,
  UserCheck,
  Clock,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
  LayoutGrid,
  Trophy,
  Bell,
  Wallet,
  Receipt,
  PieChart as PieChartIcon,
  Stethoscope,
  FlaskConical,
  Percent,
  HeartPulse,
  ClipboardCheck,
  Package,
  Settings2,
  Check,
} from "lucide-react";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Badge } from "./components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./components/ui/card";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "./components/ui/select";
import { Calendar as CalendarComponent } from "./components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./components/ui/table";
import { ScrollArea } from "./components/ui/scroll-area";
import { Separator } from "./components/ui/separator";
import { Tabs, TabsList, TabsTrigger } from "./components/ui/tabs";
import { 
  AreaChart, 
  Area, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip as RechartsTooltip, 
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell,
  PieChart,
  Pie,
  Legend
} from "recharts";
import { format, subDays, startOfMonth, startOfYear, startOfWeek } from "date-fns";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Date preset options
const DATE_PRESETS = [
  { label: "Today", value: "today" },
  { label: "This Week", value: "week" },
  { label: "This Month", value: "month" },
  { label: "Past 3 Months", value: "3months" },
  { label: "Past 6 Months", value: "6months" },
  { label: "This Year", value: "year" },
  { label: "Custom", value: "custom" }
];

// Helper to calculate date ranges
const getDateRange = (preset) => {
  const today = new Date();
  switch (preset) {
    case "today":
      return { from: today, to: today };
    case "week":
      return { from: startOfWeek(today, { weekStartsOn: 1 }), to: today };
    case "month":
      return { from: startOfMonth(today), to: today };
    case "3months":
      return { from: subDays(today, 90), to: today };
    case "6months":
      return { from: subDays(today, 180), to: today };
    case "year":
      return { from: startOfYear(today), to: today };
    default:
      return { from: startOfMonth(today), to: today };
  }
};

// Format currency in Indian format
const formatCurrency = (value) => {
  if (!value || value === 0) return "₹0";
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

// Format compact currency
const formatCompactCurrency = (value) => {
  if (!value || value === 0) return "₹0";
  if (value >= 10000000) {
    return `₹${(value / 10000000).toFixed(2)}Cr`;
  } else if (value >= 100000) {
    return `₹${(value / 100000).toFixed(2)}L`;
  } else if (value >= 1000) {
    return `₹${(value / 1000).toFixed(1)}K`;
  }
  return `₹${value.toFixed(0)}`;
};

// Format date in IST
const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  try {
    return new Date(dateStr).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric", timeZone: "Asia/Kolkata" });
  } catch {
    return dateStr;
  }
};

// Context labels based on bill type for healthcare relevance
const getContextLabels = (billType) => {
  const bt = billType || '';
  const map = {
    'IP': {
      dashboardSub: 'In-Patient Registry Dashboard',
      trendTitle: 'IP Revenue Trends', trendSub: 'Track in-patient admission revenue over time',
      doctorTitle: 'Top IP Billers', doctorSub: 'By in-patient revenue billed',
      schemeTitle: 'IP Revenue by Scheme', schemeSub: 'In-patient insurance & scheme distribution',
      unbilledTitle: 'Unbilled IP Admissions', unbilledSub: 'In-patients requiring billing attention',
      pendingTitle: 'IP Pending Payments', pendingSub: 'Top in-patient amounts to follow up',
      registryTitle: 'In-Patient Registry', emptyLabel: 'in-patient',
    },
    'OP': {
      dashboardSub: 'Out-Patient Registry Dashboard',
      trendTitle: 'OP Revenue Trends', trendSub: 'Track out-patient consultation revenue over time',
      doctorTitle: 'Top OP Billers', doctorSub: 'By out-patient revenue billed',
      schemeTitle: 'OP Revenue by Scheme', schemeSub: 'Out-patient scheme distribution',
      unbilledTitle: 'Unbilled OP Visits', unbilledSub: 'Out-patients requiring billing attention',
      pendingTitle: 'OP Pending Payments', pendingSub: 'Top out-patient amounts to follow up',
      registryTitle: 'Out-Patient Registry', emptyLabel: 'out-patient',
    },
    'Investigation': {
      dashboardSub: 'Investigation Registry Dashboard',
      trendTitle: 'Investigation Revenue', trendSub: 'Track lab & diagnostic revenue over time',
      doctorTitle: 'Top Investigation Billers', doctorSub: 'By investigation revenue billed',
      schemeTitle: 'Investigation Revenue by Scheme', schemeSub: 'Lab & diagnostic scheme distribution',
      unbilledTitle: 'Unbilled Investigations', unbilledSub: 'Investigations requiring billing attention',
      pendingTitle: 'Investigation Pending Payments', pendingSub: 'Top investigation amounts to follow up',
      registryTitle: 'Investigation Registry', emptyLabel: 'investigation',
    },
  };
  if (map[bt]) return map[bt];
  if (bt) return {
    dashboardSub: `${bt} Registry Dashboard`,
    trendTitle: `${bt} Revenue Trends`, trendSub: `Track ${bt.toLowerCase()} revenue over time`,
    doctorTitle: `Top ${bt} Billers`, doctorSub: `By ${bt.toLowerCase()} revenue billed`,
    schemeTitle: `${bt} Revenue by Scheme`, schemeSub: `${bt} scheme distribution`,
    unbilledTitle: `Unbilled ${bt}`, unbilledSub: `${bt} items requiring billing attention`,
    pendingTitle: `${bt} Pending Payments`, pendingSub: `Top ${bt.toLowerCase()} amounts to follow up`,
    registryTitle: `${bt} Registry`, emptyLabel: bt.toLowerCase(),
  };
  return {
    dashboardSub: 'Patient Registry Dashboard',
    trendTitle: 'Revenue Trends', trendSub: 'Track revenue over time',
    doctorTitle: 'Top Billers', doctorSub: 'By revenue billed',
    schemeTitle: 'Revenue by Scheme', schemeSub: 'Insurance & scheme-wise distribution',
    unbilledTitle: 'Unbilled Admissions', unbilledSub: 'Patients requiring billing',
    pendingTitle: 'High Pending Payments', pendingSub: 'Top amounts to follow up',
    registryTitle: 'Patient Registry', emptyLabel: 'patient',
  };
};

// Bill-type-specific KPI configurations
const getKPIConfig = (billType, s) => {
  if (!s) s = {};
  const fmt = (v) => v?.toLocaleString('en-IN') || "0";

  const cards = {
    patients: { title: "Total Patients", value: fmt(s.total_patients), icon: Users, gradient: "from-indigo-500 to-indigo-600", pastel: "bg-indigo-50 border-indigo-100", iconBg: "bg-indigo-100", accentText: "text-indigo-600", row: "primary" },
    revenue: { title: "Total Revenue", value: formatCompactCurrency(s.total_revenue), icon: IndianRupee, gradient: "from-emerald-500 to-teal-600", pastel: "bg-emerald-50 border-emerald-100", iconBg: "bg-emerald-100", accentText: "text-emerald-600", row: "primary" },
    collected: { title: "Collected", value: formatCompactCurrency(s.collected_revenue), icon: Wallet, gradient: "from-blue-500 to-blue-600", pastel: "bg-blue-50 border-blue-100", iconBg: "bg-blue-100", accentText: "text-blue-600", row: "primary" },
    pending: { title: "Pending", value: formatCompactCurrency(s.pending_revenue), icon: Clock, gradient: "from-amber-400 to-orange-500", pastel: "bg-amber-50 border-amber-100", iconBg: "bg-amber-100", accentText: "text-amber-600", row: "primary" },
    admissions: { title: "Admissions", value: fmt(s.total_admissions), icon: BedDouble, pastel: "bg-purple-50 border-purple-100", iconBg: "bg-purple-100", accentText: "text-purple-600", row: "secondary" },
    avgRev: { title: "Avg. Revenue", value: formatCompactCurrency(s.avg_revenue_per_patient), icon: TrendingUp, pastel: "bg-teal-50 border-teal-100", iconBg: "bg-teal-100", accentText: "text-teal-600", row: "secondary" },
    bills: { title: "Total Bills", value: fmt(s.total_bills), icon: FileText, pastel: "bg-slate-50 border-slate-200", iconBg: "bg-slate-100", accentText: "text-slate-600", row: "secondary" },
    unbilled: { title: "Unbilled", value: fmt(s.unbilled_patients), icon: AlertTriangle, pastel: "bg-rose-50 border-rose-100", iconBg: "bg-rose-100", accentText: "text-rose-600", row: "secondary" },
    avgLOS: { title: "Avg. Length of Stay", value: s.avg_length_of_stay ? `${s.avg_length_of_stay} days` : "N/A", icon: HeartPulse, gradient: "from-violet-500 to-purple-600", pastel: "bg-violet-50 border-violet-100", iconBg: "bg-violet-100", accentText: "text-violet-600", row: "primary" },
    discharged: { title: "Discharged", value: fmt(s.discharged_count), icon: UserCheck, pastel: "bg-green-50 border-green-100", iconBg: "bg-green-100", accentText: "text-green-600", row: "secondary" },
    collRate: { title: "Collection Rate", value: `${s.collection_rate || 0}%`, icon: Percent, gradient: "from-teal-500 to-cyan-600", pastel: "bg-teal-50 border-teal-100", iconBg: "bg-teal-100", accentText: "text-teal-600", row: "primary" },
    avgBill: { title: "Avg. Bill Amount", value: formatCompactCurrency(s.avg_bill_amount), icon: Receipt, pastel: "bg-cyan-50 border-cyan-100", iconBg: "bg-cyan-100", accentText: "text-cyan-600", row: "secondary" },
    billedRate: { title: "Billed Rate", value: `${s.billed_ratio || 0}%`, icon: ClipboardCheck, pastel: "bg-lime-50 border-lime-100", iconBg: "bg-lime-100", accentText: "text-lime-600", row: "secondary" },
  };

  switch (billType) {
    case 'IP':
      return {
        primary: [cards.patients, cards.avgLOS, cards.revenue, cards.pending],
        secondary: [cards.admissions, cards.discharged, cards.collRate, cards.unbilled]
      };
    case 'OP':
      return {
        primary: [
          { ...cards.patients, title: "OP Patients", icon: Stethoscope },
          cards.revenue, cards.collected, cards.collRate
        ],
        secondary: [cards.bills, cards.avgBill, cards.pending, cards.unbilled]
      };
    case 'Investigation':
      return {
        primary: [
          { ...cards.patients, title: "Test Patients", icon: FlaskConical },
          cards.revenue, cards.avgBill, cards.collRate
        ],
        secondary: [cards.bills, cards.collected, cards.pending, cards.unbilled]
      };
    default:
      return {
        primary: [cards.patients, cards.revenue, cards.collected, cards.pending],
        secondary: [cards.admissions, cards.avgRev, cards.bills, cards.unbilled]
      };
  }
};

// Status Badge Component
const StatusBadge = ({ status, type = "payment" }) => {
  if (type === "billing") {
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
        status === "Billed" 
          ? "bg-emerald-100 text-emerald-800" 
          : "bg-rose-100 text-rose-800"
      }`}>
        {status || "Not Billed"}
      </span>
    );
  }
  
  const statusStyles = {
    "PAID": "bg-emerald-100 text-emerald-800",
    "Paid": "bg-emerald-100 text-emerald-800",
    "PENDING": "bg-amber-100 text-amber-800",
    "Pending": "bg-amber-100 text-amber-800",
    "UNPAID": "bg-rose-100 text-rose-800",
    "Partial": "bg-blue-100 text-blue-800"
  };
  
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${statusStyles[status] || "bg-slate-100 text-slate-800"}`}>
      {status || "-"}
    </span>
  );
};

// KPI Card — Primary: gradient bg, white text | Secondary: pastel bg, colored text
const KPICard = ({ title, value, icon: Icon, gradient, pastel, iconBg, accentText, row }) => {
  const isPrimary = row === "primary" && gradient;
  return (
    <div
      className={`relative overflow-hidden rounded-2xl p-5 transition-all duration-300 hover:shadow-xl hover:-translate-y-0.5 ${
        isPrimary
          ? `bg-gradient-to-br ${gradient} text-white shadow-lg`
          : `${pastel} border shadow-sm`
      }`}
      data-testid={`kpi-${title.toLowerCase().replace(/\s+/g, '-')}`}
    >
      {/* Decorative circles for primary cards */}
      {isPrimary && (
        <>
          <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full bg-white/10" />
          <div className="absolute -bottom-4 -left-4 w-16 h-16 rounded-full bg-white/[0.07]" />
        </>
      )}

      <div className="relative flex items-start justify-between mb-3">
        <span className={`text-xs font-bold uppercase tracking-wider ${isPrimary ? "text-white/70" : accentText + " opacity-70"}`}>
          {title}
        </span>
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${isPrimary ? "bg-white/20 backdrop-blur-sm" : iconBg}`}>
          <Icon className={`w-5 h-5 ${isPrimary ? "text-white" : accentText}`} />
        </div>
      </div>

      <p className={`relative text-3xl font-black tabular-nums tracking-tight ${isPrimary ? "text-white" : "text-slate-900"}`}>
        {value}
      </p>
    </div>
  );
};

// Doctor Leaderboard Item
const LeaderboardItem = ({ doctor, rank, avatarUrl }) => (
  <div className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors" data-testid={`leaderboard-item-${rank}`}>
    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold ${
      rank === 1 ? 'bg-amber-400 text-amber-900' :
      rank === 2 ? 'bg-slate-300 text-slate-700' :
      rank === 3 ? 'bg-orange-300 text-orange-800' :
      'bg-slate-100 text-slate-500'
    }`}>
      {rank}
    </div>
    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-400 to-purple-500 overflow-hidden flex-shrink-0">
      {avatarUrl ? (
        <img src={avatarUrl} alt={doctor.doctor_name} className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full flex items-center justify-center text-white font-semibold text-sm">
          {doctor.doctor_name?.charAt(0) || 'D'}
        </div>
      )}
    </div>
    <div className="flex-1 min-w-0">
      <p className="font-medium text-slate-800 text-sm truncate">{doctor.doctor_name || "Unknown"}</p>
      <p className="text-xs text-slate-500">{doctor.patient_count} patients</p>
    </div>
    <div className="text-right flex-shrink-0">
      <p className="font-bold text-slate-900 text-sm">{formatCompactCurrency(doctor.total_revenue)}</p>
      <p className="text-xs text-slate-400">Avg: {formatCompactCurrency(doctor.avg_revenue)}</p>
    </div>
  </div>
);

// Alert Item Component
const AlertItem = ({ alert, type }) => (
  <div className={`p-3 rounded-lg border-l-4 ${
    type === 'unbilled' 
      ? 'bg-rose-50 border-rose-400' 
      : 'bg-amber-50 border-amber-400'
  }`} data-testid={`alert-item-${alert.IPNumber}`}>
    <div className="flex items-center justify-between">
      <div className="min-w-0 flex-1">
        <p className="font-medium text-slate-800 text-sm truncate">{alert.patientName || "Unknown"}</p>
        <p className="text-xs text-slate-500 mt-0.5">IP: {alert.IPNumber}</p>
        {type === 'pending' && alert.grandTotal > 0 && (
          <p className="text-sm font-bold text-amber-700 mt-1">{formatCurrency(alert.grandTotal)}</p>
        )}
      </div>
      <span className={`text-xs font-medium px-2 py-1 rounded ${
        type === 'unbilled' 
          ? 'bg-rose-200 text-rose-800' 
          : 'bg-amber-200 text-amber-800'
      }`}>
        {type === 'unbilled' ? 'Unbilled' : alert.paymentStatus || 'Pending'}
      </span>
    </div>
  </div>
);

// Patient Detail Modal
const PatientDetailModal = ({ isOpen, onClose, patient, bills }) => (
  <Dialog open={isOpen} onOpenChange={onClose}>
    <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden" data-testid="patient-detail-modal">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <UserCheck className="w-5 h-5 text-indigo-600" />
          Patient Details
        </DialogTitle>
        <DialogDescription>
          Complete billing history for {patient?.patientName || patient?.IPNumber}
        </DialogDescription>
      </DialogHeader>
      
      <div className="space-y-6 overflow-auto max-h-[60vh] pr-2">
        {/* Patient Info Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-slate-50 rounded-lg">
          {[
            { label: "IP Number", value: patient?.IPNumber },
            { label: "MRN", value: patient?.mrn },
            { label: "Patient Name", value: patient?.patientName },
            { label: "Admission", value: formatDate(patient?.dateOfAdmission) },
            { label: "Discharge", value: formatDate(patient?.dateOfDischarge) },
            { label: "Scheme", value: patient?.schemeName },
            { label: "Billed By", value: patient?.admittedByDoctorName },
            { label: "Referred Doctor", value: patient?.referredDoctorName || "-" }
          ].map((item, idx) => (
            <div key={idx}>
              <p className="text-xs text-slate-500 font-medium uppercase tracking-wide">{item.label}</p>
              <p className="font-semibold text-slate-800 mt-0.5">{item.value || "-"}</p>
            </div>
          ))}
        </div>
        
        <Separator />
        
        {/* Billing History */}
        <div>
          <h4 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
            <Receipt className="w-4 h-4" />
            Billing History ({bills?.length || 0} records)
          </h4>
          
          {bills && bills.length > 0 ? (
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="font-semibold">Bill No</TableHead>
                    <TableHead className="font-semibold text-right">Amount</TableHead>
                    <TableHead className="font-semibold">Status</TableHead>
                    <TableHead className="font-semibold">Type</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {bills.slice(0, 20).map((bill, idx) => (
                    <TableRow key={idx}>
                      <TableCell className="font-mono text-sm">{bill.billId || "-"}</TableCell>
                      <TableCell className="text-right tabular-nums font-medium">{formatCurrency(bill.grandTotal)}</TableCell>
                      <TableCell><StatusBadge status={bill.paymentStatus} /></TableCell>
                      <TableCell className="text-slate-600">{bill.billType || "-"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500 bg-slate-50 rounded-lg">
              <FileText className="w-12 h-12 mx-auto mb-2 opacity-30" />
              <p>No billing records found</p>
            </div>
          )}
        </div>
      </div>
    </DialogContent>
  </Dialog>
);

// Main Dashboard Component
function App() {
  return (
    <>
      <Toaster position="top-right" richColors />
      <Routes>
        <Route path="/" element={<DashboardView />} />
        <Route path="/collections" element={<PaymentsReport />} />
        <Route path="/pharmacy" element={<PharmacyReport />} />
        <Route path="/pharmacy/rack-bin" element={<RackBinLocation />} />
        <Route path="/inventory" element={<InventoryReport />} />
        <Route path="/billing" element={<BillingReport />} />
      </Routes>
    </>
  );
}

function DashboardView() {
  const navigate = useNavigate();
  // State
  const [loading, setLoading] = useState(true);
  const [patients, setPatients] = useState([]);
  const [summary, setSummary] = useState(null);
  const [filterOptions, setFilterOptions] = useState(null);
  const [revenueTrends, setRevenueTrends] = useState([]);
  const [doctorLeaderboard, setDoctorLeaderboard] = useState([]);
  const [schemeBreakdown, setSchemeBreakdown] = useState([]);
  const [alerts, setAlerts] = useState({ unbilled_admissions: [], high_pending_payments: [] });
  const [pagination, setPagination] = useState({ page: 1, pageSize: 20, total: 0, totalPages: 0 });
  
  // Filters state
  const [showFilters, setShowFilters] = useState(true);
  const [datePreset, setDatePreset] = useState("month");
  const [dateRange, setDateRange] = useState(getDateRange("month"));
  const [searchQuery, setSearchQuery] = useState("");
  const [admittingDoctor, setAdmittingDoctor] = useState("");
  const [referredDoctor, setReferredDoctor] = useState("");
  const [scheme, setScheme] = useState("");
  const [paymentStatus, setPaymentStatus] = useState("");
  const [billingStatus, setBillingStatus] = useState("");
  const [billType, setBillType] = useState("");
  const [sortBy, setSortBy] = useState("dateOfAdmission");
  const [sortOrder, setSortOrder] = useState("desc");
  const [trendPeriod, setTrendPeriod] = useState("daily");
  
  // Patient detail modal
  const [patientDetail, setPatientDetail] = useState(null);
  const [patientBills, setPatientBills] = useState([]);
  const [showPatientModal, setShowPatientModal] = useState(false);

  // Column visibility
  const [hiddenColumns, setHiddenColumns] = useState(new Set());
  const [showColumnManager, setShowColumnManager] = useState(false);

  // Chart drill-down active filter
  const [chartFilter, setChartFilter] = useState(null); // { type: 'scheme'|'doctor'|'date', value, label }

  // Doctor avatars
  const doctorAvatars = [
    "https://images.unsplash.com/photo-1612943705904-e2e101abcd19?w=100&h=100&fit=crop&crop=face",
    "https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=100&h=100&fit=crop&crop=face",
    "https://images.unsplash.com/photo-1537368910025-700350fe46c7?w=100&h=100&fit=crop&crop=face",
    "https://images.unsplash.com/photo-1622253692010-333f2da6031d?w=100&h=100&fit=crop&crop=face"
  ];

  // Context-aware labels and KPIs
  const labels = useMemo(() => getContextLabels(billType), [billType]);
  const kpiCards = useMemo(() => getKPIConfig(billType, summary), [billType, summary]);

  // Column definitions for the data grid
  const columnDefs = useMemo(() => {
    const cols = [];
    cols.push({ key: "billNumber", label: billType ? `${billType} Number` : "Bill Number", alwaysVisible: true });
    cols.push({ key: "mrn", label: "MRN" });
    cols.push({ key: "patientName", label: "Patient Name", alwaysVisible: true });
    cols.push({ key: "billDate", label: "Bill Date" });
    cols.push({ key: "doctor", label: "Billed By" });
    cols.push({ key: "scheme", label: "Scheme" });
    cols.push({ key: "billNo", label: "Bill No" });
    cols.push({ key: "billCount", label: "Bills" });
    if (!billType) {
      cols.push({ key: "billType", label: "Bill Type" });
    }
    cols.push({ key: "amount", label: "Amount" });
    cols.push({ key: "payment", label: "Payment" });
    cols.push({ key: "action", label: "", alwaysVisible: true });
    return cols;
  }, [billType]);

  const isColumnVisible = useCallback((key) => !hiddenColumns.has(key), [hiddenColumns]);

  const toggleColumn = useCallback((key) => {
    setHiddenColumns(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  // Apply chart drill-down filter
  const applyChartFilter = useCallback((type, value, label) => {
    // If same filter clicked again, clear it
    if (chartFilter && chartFilter.type === type && chartFilter.value === value) {
      clearChartFilter();
      return;
    }
    setChartFilter({ type, value, label });
    if (type === "scheme") {
      setScheme(value);
    } else if (type === "doctor") {
      setAdmittingDoctor(value);
    } else if (type === "date") {
      setDatePreset("custom");
      // Handle both daily (YYYY-MM-DD) and monthly (YYYY-MM) formats
      const fromDate = new Date(value + (value.length <= 7 ? "-01" : ""));
      let toDate;
      if (value.length <= 7) {
        // Monthly: set to end of month
        toDate = new Date(fromDate.getFullYear(), fromDate.getMonth() + 1, 0);
      } else {
        toDate = new Date(fromDate);
      }
      setDateRange({ from: fromDate, to: toDate });
    }
  }, [chartFilter]);

  const clearChartFilter = useCallback(() => {
    if (!chartFilter) return;
    if (chartFilter.type === "scheme") setScheme("");
    else if (chartFilter.type === "doctor") setAdmittingDoctor("");
    else if (chartFilter.type === "date") {
      setDatePreset("month");
      setDateRange(getDateRange("month"));
    }
    setChartFilter(null);
  }, [chartFilter]);

  // Build filter params
  const buildFilterParams = useCallback(() => {
    const params = new URLSearchParams();
    if (dateRange.from) params.append("date_from", format(dateRange.from, "yyyy-MM-dd"));
    if (dateRange.to) params.append("date_to", format(dateRange.to, "yyyy-MM-dd"));
    if (searchQuery) params.append("search", searchQuery);
    if (admittingDoctor && admittingDoctor !== "all") params.append("admitting_doctor", admittingDoctor);
    if (referredDoctor && referredDoctor !== "all") params.append("referred_doctor", referredDoctor);
    if (scheme && scheme !== "all") params.append("scheme", scheme);
    if (paymentStatus && paymentStatus !== "all") params.append("payment_status", paymentStatus);
    if (billingStatus && billingStatus !== "all") params.append("billing_status", billingStatus);
    if (billType) params.append("bill_type", billType);
    return params;
  }, [dateRange, searchQuery, admittingDoctor, referredDoctor, scheme, paymentStatus, billingStatus, billType]);

  // Fetch functions
  const fetchFilterOptions = async () => {
    try {
      const response = await axios.get(`${API}/filter-options`);
      setFilterOptions(response.data);
    } catch (error) {
      console.error("Error fetching filter options:", error);
    }
  };

  const fetchPatients = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = buildFilterParams();
      params.append("page", page.toString());
      params.append("page_size", pagination.pageSize.toString());
      params.append("sort_by", sortBy);
      params.append("sort_order", sortOrder);
      
      const response = await axios.get(`${API}/patients?${params.toString()}`);
      setPatients(response.data.data);
      setPagination({
        page: response.data.page,
        pageSize: response.data.page_size,
        total: response.data.total,
        totalPages: response.data.total_pages
      });
    } catch (error) {
      console.error("Error fetching patients:", error);
      toast.error("Failed to load patient data");
    } finally {
      setLoading(false);
    }
  }, [buildFilterParams, pagination.pageSize, sortBy, sortOrder]);

  const fetchSummary = useCallback(async () => {
    try {
      const params = buildFilterParams();
      const response = await axios.get(`${API}/summary?${params.toString()}`);
      setSummary(response.data);
    } catch (error) {
      console.error("Error fetching summary:", error);
    }
  }, [buildFilterParams]);

  const fetchRevenueTrends = useCallback(async () => {
    try {
      const params = buildFilterParams();
      params.append("period", trendPeriod);
      const response = await axios.get(`${API}/revenue-trends?${params.toString()}`);
      setRevenueTrends(response.data);
    } catch (error) {
      console.error("Error fetching revenue trends:", error);
    }
  }, [buildFilterParams, trendPeriod]);

  const fetchDoctorLeaderboard = useCallback(async () => {
    try {
      const params = buildFilterParams();
      params.append("limit", "10");
      const response = await axios.get(`${API}/doctor-leaderboard?${params.toString()}`);
      setDoctorLeaderboard(response.data);
    } catch (error) {
      console.error("Error fetching doctor leaderboard:", error);
    }
  }, [buildFilterParams]);

  const fetchAlerts = useCallback(async () => {
    try {
      const params = buildFilterParams();
      const response = await axios.get(`${API}/alerts?${params.toString()}`);
      setAlerts(response.data);
    } catch (error) {
      console.error("Error fetching alerts:", error);
    }
  }, [buildFilterParams]);

  const fetchSchemeBreakdown = useCallback(async () => {
    try {
      const params = buildFilterParams();
      const response = await axios.get(`${API}/scheme-breakdown?${params.toString()}`);
      setSchemeBreakdown(response.data);
    } catch (error) {
      console.error("Error fetching scheme breakdown:", error);
    }
  }, [buildFilterParams]);

  const fetchPatientDetail = async (mrn, ipNumber) => {
    try {
      const params = {};
      if (mrn) params.mrn = mrn;
      if (ipNumber) params.ip_number = ipNumber;
      const response = await axios.get(`${API}/patient-detail`, { params });
      setPatientDetail(response.data.patient);
      setPatientBills(response.data.bills);
      setShowPatientModal(true);
    } catch (error) {
      console.error("Error fetching patient detail:", error);
      toast.error("Failed to load patient details");
    }
  };

  const handleExport = async () => {
    try {
      toast.info("Generating Excel report...");
      const params = buildFilterParams();
      const response = await axios.get(`${API}/export?${params.toString()}`, { responseType: 'blob' });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `IP_Patient_Report_${format(new Date(), 'yyyyMMdd_HHmmss')}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("Report exported successfully!");
    } catch (error) {
      console.error("Error exporting:", error);
      toast.error("Failed to export report");
    }
  };

  const handleDatePresetChange = (preset) => {
    setDatePreset(preset);
    if (preset !== "custom") {
      setDateRange(getDateRange(preset));
    }
  };

  const clearFilters = () => {
    setDatePreset("month");
    setDateRange(getDateRange("month"));
    setSearchQuery("");
    setAdmittingDoctor("");
    setReferredDoctor("");
    setScheme("");
    setPaymentStatus("");
    setBillingStatus("");
    setChartFilter(null);
  };

  const refreshData = () => {
    fetchPatients(pagination.page);
    fetchSummary();
    fetchRevenueTrends();
    fetchDoctorLeaderboard();
    fetchAlerts();
    fetchSchemeBreakdown();
    toast.success("Data refreshed");
  };

  // Effects
  useEffect(() => {
    fetchFilterOptions();
  }, []);

  // Refetch all data when billType changes
  useEffect(() => {
    // Clear stale data immediately to prevent wrong data display during fetch
    setAlerts({ unbilled_admissions: [], high_pending_payments: [] });
    setDoctorLeaderboard([]);
    setSchemeBreakdown([]);
    
    const fetchData = async () => {
      await Promise.all([
        fetchPatients(1),
        fetchSummary(),
        fetchRevenueTrends(),
        fetchDoctorLeaderboard(),
        fetchAlerts(),
        fetchSchemeBreakdown()
      ]);
    };
    fetchData();
  }, [billType, dateRange, searchQuery, admittingDoctor, scheme, paymentStatus, billingStatus]);

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("desc");
    }
  };

  return (
    <div className="min-h-screen bg-slate-100" data-testid="dashboard-container" onClick={(e) => {
      // Close column manager dropdown when clicking outside
      if (showColumnManager && !e.target.closest('[data-testid="column-visibility-btn"]') && !e.target.closest('[data-testid="column-visibility-dropdown"]')) {
        setShowColumnManager(false);
      }
    }}>
      <Toaster richColors position="top-right" />
      
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50" data-testid="dashboard-header">
        <div className="max-w-[1440px] mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg shadow-indigo-200">
              <Activity className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-slate-900">eCare 360</h1>
              <p className="text-xs text-slate-500">{labels.dashboardSub}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={refreshData} data-testid="refresh-btn">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Button size="sm" onClick={handleExport} className="bg-indigo-600 hover:bg-indigo-700" data-testid="export-btn">
              <Download className="w-4 h-4 mr-2" />
              Export Excel
            </Button>
            <Button size="sm" variant="outline" onClick={() => navigate("/collections")} className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="nav-payments-report">
              <IndianRupee className="w-4 h-4 mr-1" />
              Collections Report
            </Button>
            <Button size="sm" variant="outline" onClick={() => navigate("/pharmacy")} className="border-teal-300 text-teal-700 hover:bg-teal-50" data-testid="nav-pharmacy-report">
              <Activity className="w-4 h-4 mr-1" />
              Pharmacy Report
            </Button>
            <Button size="sm" variant="outline" onClick={() => navigate("/inventory")} className="border-purple-300 text-purple-700 hover:bg-purple-50" data-testid="nav-inventory-report">
              <Package className="w-4 h-4 mr-1" />
              Inventory Report
            </Button>
            <Button size="sm" variant="outline" onClick={() => navigate("/billing")} className="border-blue-300 text-blue-700 hover:bg-blue-50" data-testid="nav-billing-report">
              <FileText className="w-4 h-4 mr-1" />
              Billing Report
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-[1440px] mx-auto px-6 py-6 space-y-6">
        {/* Billing Type Tabs - Dynamic */}
        <div className="flex items-center gap-1 bg-white rounded-xl border border-slate-200 p-1.5 overflow-x-auto" data-testid="billing-type-tabs">
          <button
            onClick={() => setBillType("")}
            className={`px-5 py-2 text-sm font-semibold rounded-lg transition-all whitespace-nowrap ${
              billType === "" 
                ? "bg-indigo-600 text-white shadow-md" 
                : "text-slate-600 hover:bg-slate-100"
            }`}
            data-testid="tab-all"
          >
            All
          </button>
          {filterOptions?.bill_types?.map((bt) => (
            <button
              key={bt}
              onClick={() => setBillType(bt)}
              className={`px-5 py-2 text-sm font-semibold rounded-lg transition-all whitespace-nowrap ${
                billType === bt 
                  ? "bg-indigo-600 text-white shadow-md" 
                  : "text-slate-600 hover:bg-slate-100"
              }`}
              data-testid={`tab-${bt.toLowerCase()}`}
            >
              {bt}
            </button>
          ))}
        </div>

        {/* Filter Section */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="filter-panel">
          <button 
            className="w-full flex items-center justify-between p-4 hover:bg-slate-50 transition-colors"
            onClick={() => setShowFilters(!showFilters)}
          >
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-indigo-600" />
              <span className="font-semibold text-slate-700">Advanced Filters</span>
              {(admittingDoctor || scheme || paymentStatus || billingStatus || searchQuery) && (
                <span className="px-2 py-0.5 bg-indigo-100 text-indigo-700 text-xs font-medium rounded-full">Active</span>
              )}
            </div>
            {showFilters ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
          </button>
          
          {showFilters && (
            <div className="border-t border-slate-200 p-5 space-y-5">
              {/* Date Presets Row */}
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 block">Admission Date</label>
                <div className="flex flex-wrap gap-2">
                  {DATE_PRESETS.map((preset) => (
                    <button
                      key={preset.value}
                      onClick={() => handleDatePresetChange(preset.value)}
                      className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                        datePreset === preset.value 
                          ? "bg-indigo-600 text-white shadow-md" 
                          : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                      }`}
                      data-testid={`date-preset-${preset.value}`}
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>
                
                {datePreset === "custom" && (
                  <div className="flex items-center gap-3 mt-3">
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-[160px]" data-testid="date-from-picker">
                          <Calendar className="w-4 h-4 mr-2" />
                          {dateRange.from ? format(dateRange.from, "dd MMM yyyy") : "From"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <CalendarComponent mode="single" selected={dateRange.from} onSelect={(date) => setDateRange({ ...dateRange, from: date })} />
                      </PopoverContent>
                    </Popover>
                    <span className="text-slate-400">to</span>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-[160px]" data-testid="date-to-picker">
                          <Calendar className="w-4 h-4 mr-2" />
                          {dateRange.to ? format(dateRange.to, "dd MMM yyyy") : "To"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <CalendarComponent mode="single" selected={dateRange.to} onSelect={(date) => setDateRange({ ...dateRange, to: date })} />
                      </PopoverContent>
                    </Popover>
                  </div>
                )}
              </div>
              
              {/* Filters Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4">
                <div className="lg:col-span-2 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input 
                    placeholder="Search IP, MRN, or Name..." 
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 h-10"
                    data-testid="search-input"
                  />
                </div>
                
                <Select value={admittingDoctor} onValueChange={setAdmittingDoctor}>
                  <SelectTrigger className="h-10" data-testid="admitting-doctor-select">
                    <SelectValue placeholder="Billed By" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Billers</SelectItem>
                    {filterOptions?.admitting_doctors?.map((doc) => (
                      <SelectItem key={doc} value={doc}>{doc}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                <Select value={scheme} onValueChange={setScheme}>
                  <SelectTrigger className="h-10" data-testid="scheme-select">
                    <SelectValue placeholder="Scheme" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Schemes</SelectItem>
                    {filterOptions?.schemes?.map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                <Select value={paymentStatus} onValueChange={setPaymentStatus}>
                  <SelectTrigger className="h-10" data-testid="payment-status-select">
                    <SelectValue placeholder="Payment" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    {filterOptions?.payment_statuses?.map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                <Button variant="ghost" onClick={clearFilters} className="h-10 text-slate-500 hover:text-slate-700" data-testid="clear-filters-btn">
                  <X className="w-4 h-4 mr-1" />
                  Clear
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* KPI Cards - Primary Row */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="summary-section">
          {kpiCards.primary.map((card) => (
            <KPICard key={card.title} {...card} row="primary" />
          ))}
        </div>

        {/* KPI Cards - Secondary Row */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {kpiCards.secondary.map((card) => (
            <KPICard key={card.title} {...card} row="secondary" />
          ))}
        </div>

        {/* Charts and Leaderboard Row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Revenue Trends Chart */}
          <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 p-5" data-testid="revenue-chart-card">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-bold text-slate-800">{labels.trendTitle}</h3>
                <p className="text-sm text-slate-500">{labels.trendSub} <span className="text-indigo-400 text-xs ml-1">Click to filter</span></p>
              </div>
              <div className="flex bg-slate-100 rounded-lg p-1">
                <button 
                  onClick={() => setTrendPeriod("daily")}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${trendPeriod === "daily" ? "bg-white shadow text-slate-900" : "text-slate-500"}`}
                  data-testid="trend-daily"
                >
                  Daily
                </button>
                <button 
                  onClick={() => setTrendPeriod("monthly")}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${trendPeriod === "monthly" ? "bg-white shadow text-slate-900" : "text-slate-500"}`}
                  data-testid="trend-monthly"
                >
                  Monthly
                </button>
              </div>
            </div>
            <div className="h-[280px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={revenueTrends} onClick={(e) => {
                  if (e && e.activePayload && e.activePayload[0]) {
                    const d = e.activePayload[0].payload;
                    if (d.date) applyChartFilter("date", d.raw_date || d.date, d.date);
                  }
                }} style={{ cursor: "pointer" }}>
                  <defs>
                    <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#6366F1" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#6366F1" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#64748B' }} tickLine={false} axisLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: '#64748B' }} tickLine={false} axisLine={false} tickFormatter={(v) => `₹${(v/1000).toFixed(0)}k`} width={60} />
                  <RechartsTooltip 
                    contentStyle={{ backgroundColor: '#fff', border: '1px solid #E2E8F0', borderRadius: '8px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }}
                    formatter={(value) => [formatCurrency(value), 'Revenue']}
                  />
                  <Area type="monotone" dataKey="revenue" stroke="#6366F1" strokeWidth={2} fill="url(#revenueGradient)" activeDot={{ r: 6, strokeWidth: 2, stroke: '#6366F1', fill: '#fff' }} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Doctor Leaderboard */}
          <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="leaderboard-card">
            <div className="flex items-center gap-2 mb-4">
              <Trophy className="w-5 h-5 text-amber-500" />
              <div>
                <h3 className="font-bold text-slate-800">{labels.doctorTitle}</h3>
                <p className="text-sm text-slate-500">{labels.doctorSub} <span className="text-indigo-400 text-xs ml-1">Click to filter</span></p>
              </div>
            </div>
            <div className="space-y-1 max-h-[280px] overflow-y-auto">
              {doctorLeaderboard.length > 0 ? (
                doctorLeaderboard.slice(0, 8).map((doctor, idx) => (
                  <div key={doctor.doctor_name}
                    onClick={() => applyChartFilter("doctor", doctor.doctor_name, doctor.doctor_name)}
                    className={`cursor-pointer rounded-lg transition-all ${chartFilter?.type === "doctor" && chartFilter?.value === doctor.doctor_name ? "ring-2 ring-indigo-400 bg-indigo-50/50" : ""}`}
                    data-testid={`doctor-drill-${idx}`}>
                    <LeaderboardItem doctor={doctor} rank={idx + 1} avatarUrl={doctorAvatars[idx % doctorAvatars.length]} />
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <Trophy className="w-10 h-10 mx-auto mb-2 opacity-20" />
                  <p className="text-sm">No data available</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Scheme Breakdown Chart */}
        <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="scheme-breakdown-card">
          <div className="flex items-center gap-2 mb-4">
            <PieChartIcon className="w-5 h-5 text-purple-500" />
            <div>
              <h3 className="font-bold text-slate-800">{labels.schemeTitle}</h3>
              <p className="text-sm text-slate-500">{labels.schemeSub} <span className="text-indigo-400 text-xs ml-1">Click to filter</span></p>
            </div>
          </div>
          
          {schemeBreakdown.length > 0 ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Bar Chart */}
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={schemeBreakdown.slice(0, 6)} layout="vertical" margin={{ left: 20, right: 20 }}
                    onClick={(e) => {
                      if (e && e.activePayload && e.activePayload[0]) {
                        const d = e.activePayload[0].payload;
                        if (d.scheme_name) applyChartFilter("scheme", d.scheme_name, d.scheme_name);
                      }
                    }} style={{ cursor: "pointer" }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" horizontal={true} vertical={false} />
                    <XAxis type="number" tick={{ fontSize: 11, fill: '#64748B' }} tickLine={false} axisLine={false} tickFormatter={(v) => `₹${(v/100000).toFixed(1)}L`} />
                    <YAxis type="category" dataKey="scheme_name" tick={{ fontSize: 11, fill: '#64748B' }} tickLine={false} axisLine={false} width={100} />
                    <RechartsTooltip 
                      contentStyle={{ backgroundColor: '#fff', border: '1px solid #E2E8F0', borderRadius: '8px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }}
                      formatter={(value, name) => [formatCurrency(value), name === 'total_revenue' ? 'Revenue' : name]}
                    />
                    <Bar dataKey="total_revenue" radius={[0, 4, 4, 0]}>
                      {schemeBreakdown.slice(0, 6).map((entry, index) => (
                        <Cell key={`cell-${index}`}
                          fill={chartFilter?.type === "scheme" && chartFilter?.value === entry.scheme_name
                            ? '#4F46E5'
                            : ['#6366F1', '#8B5CF6', '#A855F7', '#C084FC', '#D8B4FE', '#E9D5FF'][index % 6]}
                          stroke={chartFilter?.type === "scheme" && chartFilter?.value === entry.scheme_name ? '#312E81' : 'none'}
                          strokeWidth={chartFilter?.type === "scheme" && chartFilter?.value === entry.scheme_name ? 2 : 0}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              
              {/* Scheme Details List */}
              <div className="space-y-3">
                {schemeBreakdown.slice(0, 6).map((scheme, idx) => (
                  <div key={scheme.scheme_name}
                    onClick={() => applyChartFilter("scheme", scheme.scheme_name, scheme.scheme_name)}
                    className={`flex items-center justify-between p-3 bg-slate-50 rounded-lg border cursor-pointer transition-all hover:bg-slate-100 ${
                      chartFilter?.type === "scheme" && chartFilter?.value === scheme.scheme_name
                        ? "border-indigo-400 ring-2 ring-indigo-200 bg-indigo-50/50"
                        : "border-slate-100"
                    }`}
                    data-testid={`scheme-drill-${idx}`}>
                    <div className="flex items-center gap-3">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: ['#6366F1', '#8B5CF6', '#A855F7', '#C084FC', '#D8B4FE', '#E9D5FF'][idx % 6] }} />
                      <div>
                        <p className="font-medium text-slate-800 text-sm">{scheme.scheme_name}</p>
                        <p className="text-xs text-slate-500">{scheme.patient_count} patients</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-slate-900 text-sm">{formatCompactCurrency(scheme.total_revenue)}</p>
                      <p className="text-xs text-slate-500">Avg: {formatCompactCurrency(scheme.avg_revenue)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-center py-12 text-slate-500">
              <PieChartIcon className="w-12 h-12 mx-auto mb-3 opacity-20" />
              <p>No scheme data available</p>
            </div>
          )}
        </div>

        {/* Alerts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Unbilled */}
          <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="unbilled-alerts-card">
            <div className="flex items-center gap-2 mb-4">
              <Bell className="w-5 h-5 text-rose-500" />
              <div>
                <h3 className="font-bold text-slate-800">{labels.unbilledTitle}</h3>
                <p className="text-sm text-slate-500">{labels.unbilledSub}</p>
              </div>
            </div>
            <div className="space-y-2 max-h-[200px] overflow-y-auto">
              {alerts.unbilled_admissions?.length > 0 ? (
                alerts.unbilled_admissions.map((alert, idx) => (
                  <AlertItem key={`unbilled-${alert.IPNumber}-${idx}`} alert={alert} type="unbilled" />
                ))
              ) : (
                <div className="text-center py-6 text-slate-500">
                  <Bell className="w-8 h-8 mx-auto mb-2 opacity-20" />
                  <p className="text-sm">No unbilled admissions</p>
                </div>
              )}
            </div>
          </div>

          {/* Pending Payments */}
          <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="pending-alerts-card">
            <div className="flex items-center gap-2 mb-4">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              <div>
                <h3 className="font-bold text-slate-800">{labels.pendingTitle}</h3>
                <p className="text-sm text-slate-500">{labels.pendingSub}</p>
              </div>
            </div>
            <div className="space-y-2 max-h-[200px] overflow-y-auto">
              {alerts.high_pending_payments?.length > 0 ? (
                alerts.high_pending_payments.map((alert) => (
                  <AlertItem key={`pending-${alert.IPNumber}-${alert.billNo}`} alert={alert} type="pending" />
                ))
              ) : (
                <div className="text-center py-6 text-slate-500">
                  <AlertTriangle className="w-8 h-8 mx-auto mb-2 opacity-20" />
                  <p className="text-sm">No high pending payments</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Data Table - Dynamic based on bill type */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="data-table-card">
          <div className="p-5 border-b border-slate-200 flex items-center justify-between">
            <div>
              <h3 className="font-bold text-slate-800">
                {labels.registryTitle}
              </h3>
              <p className="text-sm text-slate-500">
                Showing {pagination.total > 0 ? ((pagination.page - 1) * pagination.pageSize) + 1 : 0}-{Math.min(pagination.page * pagination.pageSize, pagination.total)} of {pagination.total.toLocaleString('en-IN')} records
              </p>
            </div>
            <div className="flex items-center gap-2">
              {/* Chart Filter Active Indicator */}
              {chartFilter && (
                <div className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-50 border border-indigo-200 rounded-lg text-xs" data-testid="chart-filter-badge">
                  <span className="font-medium text-indigo-700">
                    {chartFilter.type === "scheme" ? "Scheme" : chartFilter.type === "doctor" ? "Biller" : "Date"}: {chartFilter.label}
                  </span>
                  <button onClick={clearChartFilter} className="ml-1 p-0.5 rounded hover:bg-indigo-100 text-indigo-500" data-testid="clear-chart-filter">
                    <X className="w-3 h-3" />
                  </button>
                </div>
              )}
              {/* Column Visibility Manager */}
              <div className="relative">
                <button
                  onClick={() => setShowColumnManager(!showColumnManager)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition-all ${
                    showColumnManager ? "bg-indigo-50 border-indigo-300 text-indigo-700" : "bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100"
                  }`}
                  data-testid="column-visibility-btn"
                >
                  <Settings2 className="w-3.5 h-3.5" />
                  Columns
                  {hiddenColumns.size > 0 && (
                    <span className="ml-1 px-1.5 py-0.5 bg-indigo-600 text-white rounded-full text-[10px] font-bold">{hiddenColumns.size}</span>
                  )}
                </button>
                {showColumnManager && (
                  <div className="absolute right-0 top-full mt-1 w-56 bg-white border border-slate-200 rounded-lg shadow-xl z-50 py-2" data-testid="column-visibility-dropdown">
                    <div className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-400 border-b border-slate-100 mb-1">Toggle Columns</div>
                    {columnDefs.filter(c => !c.alwaysVisible).map(col => (
                      <button
                        key={col.key}
                        onClick={() => toggleColumn(col.key)}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                        data-testid={`col-toggle-${col.key}`}
                      >
                        <div className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${
                          isColumnVisible(col.key)
                            ? "bg-indigo-600 border-indigo-600"
                            : "bg-white border-slate-300"
                        }`}>
                          {isColumnVisible(col.key) && <Check className="w-3 h-3 text-white" />}
                        </div>
                        <span className={isColumnVisible(col.key) ? "text-slate-800" : "text-slate-400"}>{col.label}</span>
                      </button>
                    ))}
                    {hiddenColumns.size > 0 && (
                      <div className="border-t border-slate-100 mt-1 pt-1 px-3">
                        <button onClick={() => setHiddenColumns(new Set())} className="text-xs text-indigo-600 hover:text-indigo-800 font-medium py-1" data-testid="show-all-columns">
                          Show All
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
          
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  {isColumnVisible("billNumber") && (
                    <TableHead className="font-semibold">
                      <button onClick={() => handleSort("IPNumber")} className="flex items-center gap-1 hover:text-indigo-600" data-testid="sort-type-number">
                        {billType ? `${billType} Number` : 'Bill Number'} <ArrowUpDown className="w-3 h-3" />
                      </button>
                    </TableHead>
                  )}
                  {isColumnVisible("mrn") && <TableHead className="font-semibold">MRN</TableHead>}
                  {isColumnVisible("patientName") && (
                    <TableHead className="font-semibold">
                      <button onClick={() => handleSort("patientName")} className="flex items-center gap-1 hover:text-indigo-600" data-testid="sort-patient-name">
                        Patient Name <ArrowUpDown className="w-3 h-3" />
                      </button>
                    </TableHead>
                  )}
                  {isColumnVisible("billDate") && (
                    <TableHead className="font-semibold">
                      <button onClick={() => handleSort("dateOfAdmission")} className="flex items-center gap-1 hover:text-indigo-600" data-testid="sort-bill-date">
                        Bill Date <ArrowUpDown className="w-3 h-3" />
                      </button>
                    </TableHead>
                  )}
                  {isColumnVisible("doctor") && <TableHead className="font-semibold">Billed By</TableHead>}
                  {isColumnVisible("scheme") && <TableHead className="font-semibold">Scheme</TableHead>}
                  {isColumnVisible("billNo") && <TableHead className="font-semibold">Bill No</TableHead>}
                  {isColumnVisible("billCount") && <TableHead className="font-semibold text-center">Bills</TableHead>}
                  {!billType && isColumnVisible("billType") && <TableHead className="font-semibold">Bill Type</TableHead>}
                  {isColumnVisible("amount") && <TableHead className="font-semibold text-right">Amount</TableHead>}
                  {isColumnVisible("payment") && <TableHead className="font-semibold">Payment</TableHead>}
                  {isColumnVisible("action") && <TableHead className="w-10"></TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  Array.from({ length: 10 }).map((_, idx) => (
                    <TableRow key={idx}>
                      {columnDefs.filter(c => isColumnVisible(c.key)).map((_, colIdx) => (
                        <TableCell key={colIdx}><div className="h-4 w-full bg-slate-100 rounded animate-pulse" /></TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : patients.length > 0 ? (
                  patients.map((patient, idx) => (
                    <TableRow key={`${patient.ip_number}-${idx}`} className="hover:bg-slate-50" data-testid={`patient-row-${patient.ip_number}`}>
                      {isColumnVisible("billNumber") && (
                        <TableCell className="font-medium text-indigo-600">
                          {patient.patient_type_number || patient.ip_number || "-"}
                        </TableCell>
                      )}
                      {isColumnVisible("mrn") && <TableCell className="font-mono text-sm text-slate-600">{patient.mrn || "-"}</TableCell>}
                      {isColumnVisible("patientName") && <TableCell className="font-medium text-slate-800">{patient.patient_name || "-"}</TableCell>}
                      {isColumnVisible("billDate") && <TableCell className="text-slate-600">{formatDate(patient.bill_date)}</TableCell>}
                      {isColumnVisible("doctor") && <TableCell className="text-slate-600 max-w-[150px] truncate">{patient.admitted_by_doctor_name || "-"}</TableCell>}
                      {isColumnVisible("scheme") && <TableCell className="text-slate-600">{patient.scheme_name || "-"}</TableCell>}
                      {isColumnVisible("billNo") && <TableCell className="font-mono text-sm text-slate-600">{patient.bill_no || "-"}</TableCell>}
                      {isColumnVisible("billCount") && <TableCell className="text-center text-slate-600">{patient.bill_count || 0}</TableCell>}
                      {!billType && isColumnVisible("billType") && (
                        <TableCell>
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-700">
                            {patient.billable || "-"}
                          </span>
                        </TableCell>
                      )}
                      {isColumnVisible("amount") && <TableCell className="text-right tabular-nums font-semibold text-slate-800">{formatCurrency(patient.grand_total)}</TableCell>}
                      {isColumnVisible("payment") && <TableCell><StatusBadge status={patient.payment_status} /></TableCell>}
                      {isColumnVisible("action") && (
                        <TableCell>
                          <button onClick={() => fetchPatientDetail(patient.mrn, patient.ip_number)} className="p-1.5 rounded hover:bg-slate-100" data-testid={`view-patient-${patient.mrn || patient.ip_number}`}>
                            <Eye className="w-4 h-4 text-slate-400 hover:text-indigo-600" />
                          </button>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={columnDefs.filter(c => isColumnVisible(c.key)).length} className="h-32 text-center">
                      <Users className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                      <p className="text-slate-500">No {labels.emptyLabel || 'patient'} records found</p>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
          
          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="flex items-center justify-between p-4 border-t border-slate-200" data-testid="pagination">
              <p className="text-sm text-slate-500">Page {pagination.page} of {pagination.totalPages}</p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => fetchPatients(pagination.page - 1)} disabled={pagination.page <= 1} data-testid="prev-page-btn">
                  <ChevronLeft className="w-4 h-4 mr-1" /> Previous
                </Button>
                <Button variant="outline" size="sm" onClick={() => fetchPatients(pagination.page + 1)} disabled={pagination.page >= pagination.totalPages} data-testid="next-page-btn">
                  Next <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Patient Detail Modal */}
      <PatientDetailModal isOpen={showPatientModal} onClose={() => setShowPatientModal(false)} patient={patientDetail} bills={patientBills} />
    </div>
  );
}

export default App;
